import { MouseEvent, useEffect, useState } from 'react';
import { Badge, Box, Fab, Popover } from '@mui/material';
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';

import CardComments from '../comments/CardComments';

import type { IdeaComment } from '../../../interfaces/IdeaComment';
import type { Participant } from '../../../interfaces/Participant';

interface CardDetailCommentsPopoverProps {
  ideaId: string;
  comments: IdeaComment[];
  participants: Participant[];
}

const CardDetailCommentsPopover = ({
  ideaId,
  comments,
  participants,
}: CardDetailCommentsPopoverProps) => {
  const [commentsAnchorEl, setCommentsAnchorEl] = useState<HTMLElement | null>(null);
  const isCommentsPopoverOpen = Boolean(commentsAnchorEl);

  useEffect(() => {
    setCommentsAnchorEl(null);
  }, [ideaId]);

  const togglePopover = (event: MouseEvent<HTMLElement>) => {
    setCommentsAnchorEl((previousValue) =>
      previousValue ? null : (event.currentTarget as HTMLElement)
    );
  };

  return (
    <>
      <Fab
        color="primary"
        size="medium"
        aria-label={`Show comments for idea ${ideaId}`}
        aria-describedby={isCommentsPopoverOpen ? `idea-comments-popover-${ideaId}` : undefined}
        onClick={togglePopover}
        sx={{
          position: 'absolute',
          right: 16,
          bottom: 16,
          zIndex: 2,
          backgroundColor: '#04ceff',
        }}
      >
        <Badge
          badgeContent={comments.length}
          color="error"
          max={99}
          overlap="circular"
          sx={{ '& .MuiBadge-badge': { top: 2, right: 0 } }}
        >
          <ModeCommentOutlinedIcon />
        </Badge>
      </Fab>
      <Popover
        id={`idea-comments-popover-${ideaId}`}
        open={isCommentsPopoverOpen}
        anchorEl={commentsAnchorEl}
        onClose={() => setCommentsAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 'calc(100vw - 32px)',
            maxWidth: 400,
            height: '70vh',
            maxHeight: 520,
            overflow: 'hidden',
            left: { xs: '16px', sm: 'auto' },
            borderRadius: 2,
          },
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <Box sx={{ overflow: 'auto', height: '100%', pb: 2 }}>
            <CardComments ideaId={ideaId} comments={comments} participants={participants} />
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default CardDetailCommentsPopover;
